import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Map routes to required navigation permissions
const ROUTE_PERMISSIONS = {
  '/orders': 'nav.orders',
  '/leads': 'nav.leads',
  '/inventory': 'nav.inventory',
  '/vendors': 'nav.vendors',
  '/purchase-orders': 'nav.purchase_orders',
  '/deliveries': 'nav.deliveries',
  '/tasks': 'nav.tasks',
  '/marketing': 'nav.marketing',
  '/integrations/shopify': 'nav.shopify',
  '/cash': 'nav.cash',
  '/expenses': 'nav.expenses',
  '/my-expenses': 'nav.my_expenses',
  '/incentives': 'nav.incentives',
  '/my-sales': 'incentives.performance_view_own',
  '/sales-performance': 'incentives.performance_view_all',
  '/sales-targets': 'incentives.targets_view',
  '/product-availability': 'nav.product_availability',
  '/announcements': 'nav.announcements',
  '/users': 'nav.users',
  '/roles': 'nav.roles',
  '/settings': 'nav.settings',
};

// Map routes to module names for module-based access control
const ROUTE_TO_MODULE = {
  '/dashboard': 'dashboard',
  '/orders': 'orders',
  '/my-deliveries': 'orders',
  '/leads': 'leads',
  '/customers': 'customers',
  '/inventory': 'inventory',
  '/vendors': 'vendors',
  '/vendor-pickups': 'vendors',  // Vendor pickups is part of vendors module
  '/purchase-orders': 'purchase_orders',
  '/deliveries': 'deliveries',
  '/tasks': 'tasks',
  '/marketing': 'marketing',
  '/integrations': 'integrations',
  '/cash': 'cash_management',
  '/expenses': 'expenses',
  '/my-expenses': 'expenses',
  '/incentives': 'incentives',
  '/announcements': 'announcements',
  '/hr': 'hr',
};

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [userPermissions, setUserPermissions] = useState(null);
  const [enabledModules, setEnabledModules] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  useEffect(() => {
    const fetchPermissionsAndModules = async () => {
      if (!user?.id) {
        setPermissionsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        // Fetch permissions
        const permResponse = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (permResponse.data.is_admin) {
          setUserPermissions(['all']);
        } else {
          setUserPermissions(permResponse.data.permissions || []);
        }
        
        // Fetch enabled modules from /auth/me
        const meResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (meResponse.data.modules_enabled) {
          setEnabledModules(meResponse.data.modules_enabled);
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setUserPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissionsAndModules();
  }, [user?.id]);

  // Show loading while checking auth or permissions
  if (loading || permissionsLoading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wt-gold" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  const hasPermission = (permission) => {
    if (!userPermissions) return false;
    if (userPermissions.includes('all')) return true; // Admin
    return userPermissions.includes(permission);
  };

  // Check if module is enabled for current route
  const isModuleEnabled = () => {
    // Super admins without company_id can access everything
    if (user?.is_super_admin && !user?.company_id) return true;
    
    // If modules not configured, allow all
    if (!enabledModules) return true;
    
    // Find which module this route belongs to
    const currentPath = location.pathname;
    
    // Always allow these system routes
    const systemRoutes = ['/users', '/roles', '/settings', '/api-keys', '/audit-log', '/super-admin', '/profile'];
    if (systemRoutes.some(r => currentPath.startsWith(r))) return true;
    
    // Check module access
    for (const [routePrefix, moduleName] of Object.entries(ROUTE_TO_MODULE)) {
      if (currentPath.startsWith(routePrefix)) {
        // If module is explicitly disabled, block access
        if (enabledModules[moduleName] === false) {
          return false;
        }
        break;
      }
    }
    
    return true;
  };

  // Check module access first
  if (!isModuleEnabled()) {
    return <Navigate to="/dashboard" replace />;
  }

  // If requiredPermission is specified, check it
  if (requiredPermission) {
    if (!hasPermission(requiredPermission)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
